import React, { useState, useEffect, useCallback } from 'react';
import { getAdminTables } from '../api';
import {
  ArrowLeft, Table, MagnifyingGlass, CaretDown, CaretUp,
  SpinnerGap, Tag, Eye
} from '@phosphor-icons/react';

const CATEGORY_COLORS = {
  'Auth': 'bg-violet-500/15 text-violet-700',
  'Config': 'bg-blue-500/15 text-blue-700',
  'Operational': 'bg-emerald-500/15 text-emerald-700',
  'AI': 'bg-purple-500/15 text-purple-700',
  'Governance': 'bg-amber-500/15 text-amber-700',
  'Notifications': 'bg-orange-500/15 text-orange-700',
  'Cache': 'bg-slate-400/15 text-slate-600',
  'Reports': 'bg-teal-500/15 text-teal-700',
  'System': 'bg-slate-400/15 text-slate-500',
};

export default function AdminExplorer({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await getAdminTables();
      setData(d);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) return (
    <div className="flex items-center justify-center h-64"><SpinnerGap size={32} className="text-primary animate-spin" /></div>
  );

  const categories = ['All', ...new Set(data.tables.map(t => t.category))];
  const filtered = data.tables.filter(t => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'All' || t.category === categoryFilter;
    return matchSearch && matchCat;
  });
  const totalRecords = data.tables.reduce((a, t) => a + t.record_count, 0);

  return (
    <div className="space-y-5" data-testid="admin-explorer">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-background border border-border text-text-secondary hover:text-text-primary transition-all" data-testid="explorer-back-btn">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-extrabold text-text-primary">Semantic Data Explorer</h1>
          <p className="font-body text-xs text-text-secondary mt-0.5">Explore database tables powering analytics, tiles, and charts.</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Collections', value: data.total_collections },
          { label: 'Total Records', value: totalRecords.toLocaleString() },
          { label: 'Showing', value: filtered.length },
        ].map((s, i) => (
          <div key={i} className="bg-surface border border-border rounded-xl p-4 text-center">
            <p className="font-heading text-2xl font-bold text-text-primary">{s.value}</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary" />
          <input
            data-testid="explorer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tables..."
            className="w-full pl-9 pr-4 py-2.5 bg-surface border border-border rounded-lg font-body text-sm text-text-primary placeholder:text-tertiary focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {categories.map(c => (
            <button key={c} onClick={() => setCategoryFilter(c)}
              className={`px-3 py-2 rounded-lg text-xs font-body font-medium transition-all ${categoryFilter === c ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary hover:border-primary hover:text-primary'}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Tables List */}
      <div className="space-y-2" data-testid="explorer-table-list">
        {filtered.map((tbl) => {
          const isOpen = expanded === tbl.name;
          return (
            <div key={tbl.name} className="bg-surface border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-all" data-testid={`explorer-table-${tbl.name}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : tbl.name)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Table size={16} weight="duotone" className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-sm font-bold text-text-primary font-mono">{tbl.name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-body font-semibold ${CATEGORY_COLORS[tbl.category] || CATEGORY_COLORS['System']}`}>{tbl.category}</span>
                    </div>
                    <p className="font-body text-[11px] text-text-secondary truncate">{tbl.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="font-heading text-sm font-bold text-text-primary">{tbl.record_count.toLocaleString()}</p>
                    <p className="text-[10px] font-body text-tertiary">records</p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="font-heading text-sm font-bold text-text-primary">{tbl.field_count}</p>
                    <p className="text-[10px] font-body text-tertiary">fields</p>
                  </div>
                  {isOpen ? <CaretUp size={14} className="text-tertiary" /> : <CaretDown size={14} className="text-tertiary" />}
                </div>
              </button>

              {isOpen && tbl.fields.length > 0 && (
                <div className="border-t border-border bg-background/50 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye size={14} className="text-primary" />
                    <span className="text-xs font-body font-semibold text-text-primary uppercase tracking-wider">Schema &amp; Sample Data</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px] font-body">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-2 text-tertiary font-semibold uppercase tracking-wider">Field</th>
                          <th className="text-left py-2 px-2 text-tertiary font-semibold uppercase tracking-wider">Type</th>
                          <th className="text-left py-2 px-2 text-tertiary font-semibold uppercase tracking-wider">Sample Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tbl.fields.map((f, j) => (
                          <tr key={j} className="border-b border-border/30">
                            <td className="py-1.5 px-2 text-text-primary font-medium font-mono">{f.field}</td>
                            <td className="py-1.5 px-2 text-amber-600 font-mono">{f.type}</td>
                            <td className="py-1.5 px-2 text-text-secondary truncate max-w-xs">{f.sample}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {tbl.used_by && tbl.used_by.length > 0 && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50 flex-wrap">
                      <Tag size={11} className="text-tertiary" />
                      <span className="text-[10px] font-body text-tertiary uppercase tracking-wider">Used by:</span>
                      {tbl.used_by.map((v, k) => (
                        <span key={k} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-body font-medium">{v}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
